import { IsInAuthor, ReadMode } from "../state/ui";

import { getStackFromUrl, strings, text } from "@quickpoint.me/shared";
import { createBrowserHistory } from "history";
import { useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { ResumeDialog } from "../displayComponents/ResumeDialog";
import {
	getLastPageRead,
	getLocalStorageHistory,
	setLastPageRead,
} from "../lib/storage";
import { initBrowserHistory } from "../state/getPageNumberFromUrl";
import { NavigationType } from "../state/navigation";
import { PageIndex } from "../state/page";
import { NumberOfPages } from "../state/selectors";
import { StackCfKey } from "../state/stack";
import { IsInPresentationMode } from "../state/ui";

// This function records the last page viewed in a story
// If the user returns to the story the app asks if they'd like to resume from the saved page

// It also keeps track of navigation during a session.
// If the user returns to the entry point of a session it asks if thy'd like to return to that exit point.
// That record is cleared once the user has answered either way.

export const viewerHistory = createBrowserHistory();

export function QpHistory() {
	const [pageIndex, setPageIndex] = useRecoilState(PageIndex);
	const stackCfKey = useRecoilValue(StackCfKey);
	const isInAuthor = useRecoilValue(IsInAuthor);
	const readMode = useRecoilValue(ReadMode);
	const inPresentationMode = useRecoilValue(IsInPresentationMode);
	const navigationType = useRecoilValue(NavigationType);
	const numberOfPages = useRecoilValue(NumberOfPages);
	const promptedRef = useRef(false);
	// the styled replacement for the old window.confirm prompts — at most one
	// prompt is offered per visit (the three cases are mutually exclusive)
	const [resumePrompt, setResumePrompt] = useState<null | {
		message: string;
		onConfirm: () => void;
	}>(null);

	// Resume prompts. Declared BEFORE the setLastPageRead effect below: effects
	// run in declaration order, and this one must read the saved page from the
	// previous visit before setLastPageRead overwrites it with the current one.
	useEffect(() => {
		if (promptedRef.current) return;
		if (isInAuthor || inPresentationMode) return;
		if (navigationType !== "tap" || numberOfPages <= 1) return;
		if (!stackCfKey) return;
		promptedRef.current = true;

		const stackInfo = getStackFromUrl(window.location.pathname);
		if (!stackInfo.validStackCfKey) return;

		// see if this story has been part of the saved QP navigation history
		const history = getLocalStorageHistory();
		if (history && typeof history === "object" && stackInfo.stackCfKey in history) {
			const crumbs = history[stackInfo.stackCfKey].crumbs;
			const exitStory = crumbs[crumbs.length - 1];
			if (`/${stackInfo.stackCfKey}` !== exitStory) {
				// clear the record whichever way the user answers — leaving it
				// behind re-prompted on every future visit
				delete history[stackInfo.stackCfKey];
				localStorage.setItem(
					strings.localStorage.QpHistoryLocalStorage,
					JSON.stringify(history),
				);
				setResumePrompt({
					message: text.reader.resumeExitStory(exitStory),
					onConfirm: () => window.open(exitStory, "_self"),
				});
				return;
			}
		}

		const clamp = (n: number) => Math.min(Math.max(n, 1), numberOfPages);
		const previousPage = getLastPageRead(stackInfo.stackCfKey);

		// the user left the story at a different page to the one in the URL
		if (previousPage && clamp(previousPage) !== pageIndex) {
			setResumePrompt({
				message: text.reader.resumeLastPage,
				onConfirm: () => setPageIndex(clamp(previousPage)),
			});
			return;
		}

		// no saved page but the URL asks for a page beyond the start
		if (!previousPage && pageIndex > 1) {
			setResumePrompt({
				message: text.reader.resumeGoToStart,
				onConfirm: () => setPageIndex(1),
			});
		}
	}, [
		stackCfKey,
		navigationType,
		numberOfPages,
		isInAuthor,
		inPresentationMode,
		pageIndex,
		setPageIndex,
	]);

	// reset the history when scroll forces the page index to be 1
	useEffect(() => {
		if (isInAuthor || inPresentationMode) return;
		initBrowserHistory(stackCfKey, pageIndex, readMode);
	}, [stackCfKey, pageIndex, readMode, isInAuthor, inPresentationMode]);

	useEffect(() => {
		if (isInAuthor || inPresentationMode) return;
		setLastPageRead(stackCfKey, pageIndex);
	}, [pageIndex, stackCfKey, isInAuthor, inPresentationMode]);

	if (!resumePrompt) return <></>;
	return (
		<ResumeDialog
			message={resumePrompt.message}
			onConfirm={() => {
				resumePrompt.onConfirm();
				setResumePrompt(null);
			}}
			onDismiss={() => setResumePrompt(null)}
		/>
	);
}
